// Module to hold functions to assist working with HTML
import config from 'shared/config';

/**
 * Remove all HTML tags from a string
 * except bulleted lists, paragraphs, and bold font
 * @param {string} htmlString
 */
export const removeHTML = htmlString => {
	if (!htmlString) {
		return '';
	}
	// Strip out anything between < and >
	// keep ul, li, p, and strong tags
	return htmlString.replace(
		/<(?!\/?ul\s*\/?)(?!\/?li\s*\/?)(?!\/?p\s*\/?)(?!\/?strong\s*\/?)[^>]+>/gm,
		'',
	);
};

/**
 * Remove inline styling or class from HTML tags.
 * @param {string} htmlString
 */
export const removeStyleHTML = htmlString => {
	if (!htmlString) {
		return '';
	}
	// remove any inline styles
	const removeStyle = htmlString.replace(/\s*?style=".*?"/gm, '');
	// remove class
	return removeStyle.replace(/\s*?class=".*?"/gm, '');
};

/**
 * Decode any strings with HTML encoded characters
 * @param {string} htmlString - string with encoded HTML characters
 * http://stackoverflow.com/a/34064434
 */
export const decodeHTML = htmlString => {
	const doc = new DOMParser().parseFromString(htmlString, 'text/html');
	return doc.documentElement.textContent;
};

/**
 * Format a number with $, commas, and no decimal
 * @param {number} price
 * @param {boolean} keepDecimal keep decimals, default is true
 * @return {string}
 */
export const formatCurrency = (price, keepDecimal = true) => {
	// check for null or undefined but let 0 through.
	if (price == null || typeof price !== 'number') {
		return '';
	}

	// format currency - https://stackoverflow.com/questions/149055/how-can-i-format-numbers-as-money-in-javascript
	let formattedPrice = price.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

	if (!keepDecimal) {
		const decimalPos = formattedPrice.indexOf('.');
		// grab anything before the decimal place
		formattedPrice = formattedPrice.substr(0, decimalPos);
	}

	return formattedPrice;
};

/**
 * NOP's content management system allows an administrator to create topic pages using a WSIWYG editor which it then
 * converts to html. If the user adds an image or video resource the src is a local resource. This function finds
 * resources using relative paths and adds a prefix to turn the re
 * @param {string} htmlString markup returned from eSTORIS
 * @returns {string} modified html markup
 */
export const prefixResources = htmlString => {
	const tags = ['img', 'video'];
	const doc = new DOMParser().parseFromString(htmlString, 'text/html');

	// users can tweak the html themselves so they can also add absolute resources themselves
	const protocolReg = new RegExp('^https?://', 'i');
	tags.forEach(tag => {
		const elements = doc.getElementsByTagName(tag);
		// elements is a collection, not an array.
		for (let i = 0; i < elements.length; i += 1) {
			const src = elements[i].getAttribute('src');
			if (!protocolReg.test(src)) {
				elements[i].setAttribute('src', `${config.ESTORIS_BASE_PATH}${src}`);
			}
		}
	});

	return doc.documentElement.innerHTML;
};

/**
 * Converts a string to start case, which has the first letter of every word capitalized
 * and the rest of the letters lowercase. Punctuation is preserved but does not mark a boundary between words.
 * Only whitespace marks a boundary between words.
 * Examples: hello testing => Hello Testing
 * HELLO TESTING => Hello Testing
 * hello-testing1.testing2. testing3. => Hello-testing1.testing2. Testing3.
 * More info on letter case: https://en.wikipedia.org/wiki/Letter_case#Stylistic_or_specialised_usage
 * @param {string} str string to be converted to start case
 * @returns {string} string in start case
 */
export const startCase = str =>
	typeof str === 'string' ? str.toLowerCase().replace(/^.|\s./g, m => m.toUpperCase()) : str;
